import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/common/Button';
import { useAuth } from '@/hooks/useAuth';
import { useHuntStore } from '@/stores/huntStore';
import { HuntStatus } from '@gps-hunt/shared';

/**
 * Welcome screen - landing page with Create/Join options
 * This page is only shown after authentication
 */
export const WelcomeScreen: React.FC = () => {
  const navigate = useNavigate();
  const { displayName, isGuest } = useAuth();
  const { currentHunt } = useHuntStore();

  const handleCreateHunt = () => {
    navigate('/create');
  };

  const handleJoinHunt = () => {
    navigate('/join');
  };

  const handleContinueHunt = () => {
    if (currentHunt) {
      navigate(`/lobby/${currentHunt.id}`);
    }
  };

  return (
    <div className="page-fixed flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-8">
        {/* Logo/Title */}
        <div className="text-center space-y-4">
          <img 
            src="/GPSHuntLogo.png" 
            alt="GPS Hunt" 
            className="w-48 h-48 mx-auto object-contain"
          />
          <div className="h-1 w-24 bg-primary mx-auto rounded-full" />
          <p className="text-text-secondary">Find the hidden treasures</p>
        </div>

        {/* Action buttons */}
        <div className="space-y-4 pt-8">
          {!isGuest && (
            <Button
              variant="primary"
              size="lg"
              fullWidth
              onClick={handleCreateHunt}
            >
              CREATE HUNT
            </Button>
          )}

          <Button
            variant={isGuest ? "primary" : "secondary"}
            size="lg"
            fullWidth
            onClick={handleJoinHunt}
          >
            JOIN HUNT
          </Button>

          {!isGuest && (
            <button
              onClick={() => navigate('/my-hunts')}
              className="w-full text-center text-text-secondary hover:text-primary transition-colors py-2"
            >
              View My Hunts →
            </button>
          )}
        </div>

        {/* Continue playing - only show for active (non-completed) hunts */}
        {currentHunt && currentHunt.status !== HuntStatus.COMPLETED && (
          <div className="pt-8 text-center">
            <button
              onClick={handleContinueHunt}
              className="text-text-secondary hover:text-primary transition-colors"
            >
              Already playing? <span className="text-primary">Continue →</span>
            </button>
          </div>
        )}

        {/* Show current user */}
        {displayName && (
          <div className="pt-4 text-center space-y-1">
            <span className="text-text-secondary text-sm">
              Playing as <span className="text-text-primary">{displayName}</span>
              {isGuest && <span className="text-text-muted"> (Guest)</span>}
            </span>
            {isGuest && (
              <p className="text-xs text-text-muted">
                Sign in with Google to create hunts
              </p>
            )}
          </div>
        )}

        {/* Footer links */}
        <footer className="pt-8 text-center">
          <div className="flex items-center justify-center gap-4 text-sm">
            <Link 
              to="/terms" 
              className="text-text-muted hover:text-text-secondary transition-colors"
            >
              Terms
            </Link>
            <span className="text-text-muted">•</span>
            <Link 
              to="/privacy" 
              className="text-text-muted hover:text-text-secondary transition-colors"
            >
              Privacy
            </Link>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default WelcomeScreen;
